import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { brand } from '../../lib/brand';

export type FabDockSide = 'left' | 'right';

/** Lower = closer to the screen bottom within a side stack. */
export const FAB_PRIORITY = {
  a11y: 10,
  news: 10,
  storefrontCart: 20,
} as const;

/** Gap between stacked FABs (matches Tailwind gap-2). */
export const FAB_STACK_GAP_CLASS = 'gap-2';

type FabRegistration = {
  id: string;
  side: FabDockSide;
  priority: number;
};

type FabDockContextValue = {
  register: (item: FabRegistration) => void;
  unregister: (id: string) => void;
  /** Live DOM node for a registered id (updated via callback refs). */
  getSlot: (id: string) => HTMLElement | null;
  subscribeSlots: (onStoreChange: () => void) => () => void;
};

const FabDockContext = createContext<FabDockContextValue | null>(null);

function FabSlot({
  id,
  setSlotEl,
}: {
  id: string;
  setSlotEl: (id: string, el: HTMLElement | null) => void;
}) {
  const ownedRef = useRef<HTMLElement | null>(null);

  return (
    <div
      id={`floating-fab-slot-${id}`}
      className="pointer-events-auto relative"
      ref={(el) => {
        if (ownedRef.current && ownedRef.current !== el) {
          setSlotEl(id, null, ownedRef.current);
        }
        ownedRef.current = el;
        if (el) setSlotEl(id, el);
      }}
    />
  );
}

function DockColumn({
  side,
  ids,
  setSlotEl,
}: {
  side: FabDockSide;
  ids: string[];
  setSlotEl: (id: string, el: HTMLElement | null, onlyIf?: HTMLElement) => void;
}) {
  const sideStyle =
    side === 'left'
      ? ({ left: '1rem', right: 'auto' } as const)
      : ({ right: '1rem', left: 'auto' } as const);

  return (
    <div
      id={`floating-fab-dock-${side}`}
      className={`pointer-events-none fixed z-[80] flex flex-col-reverse items-center ${FAB_STACK_GAP_CLASS}`}
      style={{
        bottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
        ...sideStyle,
      }}
      aria-hidden={ids.length === 0}
    >
      {ids.map((id) => (
        <FabSlot key={id} id={id} setSlotEl={setSlotEl} />
      ))}
    </div>
  );
}

/**
 * Global left/right FAB docks. Each FloatingFab registers, then portals into a slot
 * stacked above already-registered items on that side (priority + gap).
 */
export function FloatingFabDockProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Record<string, FabRegistration>>({});
  const slotsRef = useRef(new Map<string, HTMLElement>());
  const slotListenersRef = useRef(new Set<() => void>());

  const notifySlots = useCallback(() => {
    slotListenersRef.current.forEach((listener) => listener());
  }, []);

  const setSlotEl = useCallback(
    (id: string, el: HTMLElement | null, onlyIf?: HTMLElement) => {
      if (el) {
        if (slotsRef.current.get(id) === el) return;
        slotsRef.current.set(id, el);
        notifySlots();
        return;
      }
      // Clear only the node this slot owned — ignore stale unmount after a side hop.
      if (onlyIf) {
        if (slotsRef.current.get(id) !== onlyIf) return;
        slotsRef.current.delete(id);
        notifySlots();
        return;
      }
      if (slotsRef.current.has(id)) {
        slotsRef.current.delete(id);
        notifySlots();
      }
    },
    [notifySlots],
  );

  const getSlot = useCallback((id: string) => slotsRef.current.get(id) ?? null, []);

  const subscribeSlots = useCallback((onStoreChange: () => void) => {
    slotListenersRef.current.add(onStoreChange);
    return () => {
      slotListenersRef.current.delete(onStoreChange);
    };
  }, []);

  const register = useCallback((item: FabRegistration) => {
    setItems((prev) => {
      const existing = prev[item.id];
      if (
        existing
        && existing.side === item.side
        && existing.priority === item.priority
      ) {
        return prev;
      }
      return { ...prev, [item.id]: item };
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setItems((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (slotsRef.current.has(id)) {
      slotsRef.current.delete(id);
      notifySlots();
    }
  }, [notifySlots]);

  const value = useMemo(
    () => ({ register, unregister, getSlot, subscribeSlots }),
    [register, unregister, getSlot, subscribeSlots],
  );

  const leftIds = useMemo(
    () =>
      Object.values(items)
        .filter((i) => i.side === 'left')
        .sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id))
        .map((i) => i.id),
    [items],
  );

  const rightIds = useMemo(
    () =>
      Object.values(items)
        .filter((i) => i.side === 'right')
        .sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id))
        .map((i) => i.id),
    [items],
  );

  return (
    <FabDockContext.Provider value={value}>
      {children}
      <DockColumn side="left" ids={leftIds} setSlotEl={setSlotEl} />
      <DockColumn side="right" ids={rightIds} setSlotEl={setSlotEl} />
    </FabDockContext.Provider>
  );
}

type FloatingFabProps = {
  id: string;
  side: FabDockSide;
  priority: number;
  children: ReactNode;
};

/**
 * Places children into the side dock above lower-priority FABs already registered.
 * Falls back to inline render if provider is missing.
 */
export function FloatingFab({ id, side, priority, children }: FloatingFabProps) {
  const ctx = useContext(FabDockContext);

  useLayoutEffect(() => {
    if (!ctx) return undefined;
    ctx.register({ id, side, priority });
    return () => ctx.unregister(id);
  }, [ctx, id, side, priority]);

  const slot = useSyncExternalStore(
    (onStoreChange) => {
      if (!ctx) return () => undefined;
      return ctx.subscribeSlots(onStoreChange);
    },
    () => (ctx ? ctx.getSlot(id) : null),
    () => null,
  );

  if (!ctx) {
    return <>{children}</>;
  }
  if (!slot || !slot.isConnected) return null;
  return createPortal(children, slot);
}

export function useFabDockSideForA11y(lang: string): FabDockSide {
  // Alive Album guide drawer sits on the right, so these two buttons stay on the left.
  if (brand.id === 'alivealbum') return 'left';
  return lang === 'he' ? 'left' : 'right';
}

export function useFabDockSideForNews(lang: string): FabDockSide {
  // Opposite of a11y so megaphone never shares the a11y / scroll-up column.
  return lang === 'he' ? 'right' : 'left';
}
